/**
 * Mutation hooks for Trips
 * 
 * These hooks handle trip mutations (create, update, delete, pin, like)
 * with automatic cache invalidation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query/client';
import { BACKEND_URL } from "../../config";
/**
 * Toggle pin status for a trip
 */
export function useTogglePin() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, isPinned }) => {
      if (isPinned) {
        await api.delete(`/api/trips/${tripId}/pin`);
      } else {
        await api.post(`/api/trips/${tripId}/pin`, {});
      }
      return { tripId, newState: !isPinned };
    },
    onMutate: async ({ tripId, isPinned }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.trips.pinnedIds() });
      
      // Snapshot the previous value
      const previousIds = queryClient.getQueryData(queryKeys.trips.pinnedIds());
      
      // Optimistically update
      queryClient.setQueryData(queryKeys.trips.pinnedIds(), (old) => {
        if (!old) return old;
        const newSet = new Set(old);
        if (isPinned) {
          newSet.delete(tripId);
        } else {
          newSet.add(tripId);
        }
        return newSet;
      });
      
      return { previousIds };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousIds) {
        queryClient.setQueryData(queryKeys.trips.pinnedIds(), context.previousIds);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.pinnedIds() });
    },
  });
}

/**
 * Toggle like status for a trip
 */
export function useToggleLike() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, isLiked }) => {
      let response;
      if (isLiked) {
        response = await api.delete(`/api/trips/${tripId}/like`);
      } else {
        response = await api.post(`/api/trips/${tripId}/like`, {});
      }
      return { tripId, newState: !isLiked, likeCount: response.data?.like_count };
    },
    onMutate: async ({ tripId, isLiked }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.trips.likedIds() });
      
      const previousIds = queryClient.getQueryData(queryKeys.trips.likedIds());
      
      queryClient.setQueryData(queryKeys.trips.likedIds(), (old) => {
        if (!old) return old;
        const newSet = new Set(old);
        if (isLiked) {
          newSet.delete(tripId);
        } else {
          newSet.add(tripId);
        }
        return newSet;
      });
      
      return { previousIds };
    },
    onError: (err, variables, context) => {
      if (context?.previousIds) {
        queryClient.setQueryData(queryKeys.trips.likedIds(), context.previousIds);
      }
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.likedIds() });
      // Also invalidate the specific trip to update like count
      if (data?.tripId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(data.tripId) });
      }
    },
  });
}

/**
 * Delete a trip
 */
export function useDeleteTrip() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (tripId) => {
      await api.delete(`/api/trips/${tripId}`);
      return tripId;
    },
    onSuccess: () => {
      // Invalidate all trip-related queries (personal + club lists)
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.all() });
      queryClient.invalidateQueries({ queryKey: ['club-trips'] });
    },
  });
}

/**
 * Upload a photo to a trip segment
 */
export function useUploadTripPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, segmentId, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await api.post(
        `/api/trips/${tripId}/segments/${segmentId}/photos`,
        formData,
        { 
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      return response.data;
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}

/**
 * Delete a photo from a trip segment
 */
export function useDeleteTripPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, segmentId, photoId }) => {
      await api.delete(
        `/api/trips/${tripId}/segments/${segmentId}/photos/${photoId}`
      );
      return { tripId, segmentId, photoId };
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}

/**
 * Update photo caption
 */
export function useUpdatePhotoCaption() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, segmentId, photoId, caption }) => {
      await api.put(
        `/api/trips/${tripId}/segments/${segmentId}/photos/${photoId}`,
        { caption }
      );
      return { tripId, segmentId, photoId, caption };
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}

/**
 * Reorder photos in a segment
 */
export function useReorderPhotos() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, segmentId, photoOrders }) => {
      await api.put(
        `/api/trips/${tripId}/segments/${segmentId}/photos/reorder`,
        { photo_orders: photoOrders }
      );
      return { tripId, segmentId };
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}

/**
 * Set main photo for trip
 */
export function useSetMainPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, filename }) => {
      await api.put(
        `/api/trips/${tripId}/main-photo`,
        { main_photo: filename }
      );
      return { tripId, filename };
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}


/**
 * Upload a photo directly to trip (legacy support)
 */
export function useUploadTripLevelPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await api.post(
        `/api/trips/${tripId}/photos`,
        formData,
        { 
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      return response.data;
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}

/**
 * Delete a photo from trip level (legacy support)
 */
export function useDeleteTripLevelPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, filename }) => {
      await api.delete(
        `/api/trips/${tripId}/photos/${encodeURIComponent(filename)}`
      );
      return { tripId, filename };
    },
    onSuccess: (_, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
    },
  });
}


/**
 * Clone a trip
 */
export function useCloneTrip() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (tripId) => {
      const response = await api.post(
        `/api/trips/${tripId}/clone`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.myTrips() });
      queryClient.invalidateQueries({ queryKey: ['club-trips'] });
    },
  });
}

/**
 * Migrate a trip to a club
 */
export function useMigrateTrip() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tripId, clubId, targetOwnerType = "club" }) => {
      const response = await api.post(
        `/api/trips/${tripId}/migrate-context`,
        { 
          target_owner_type: targetOwnerType,
          club_id: clubId 
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.trips.myTrips() });
      queryClient.invalidateQueries({ queryKey: ['club-trips'] });
    },
  });
}

